import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from "react-redux";
import { MainPageWithButtons } from "../../Common/_components/MainPageWithButtons";
import { InlineEditButtons } from '../_components/InlineEditButtons';
import { FILTER_DISABLED_ALL } from "../_constants";
import { appPermissions } from '../_constants/permissions.constants';
import { TimeHelper } from '../_helpers';
import { LocationInfo } from "../_models/LocationInfo";
import { Selectors } from '../_reducers/app.reducer';
import { ClockinActions } from '../_stores/ClockinStore/ClockinActions';
import { ClockinsFilters } from "./ClockinsFilters";
import { UsersSettingsColumn } from './UsersSettingColumn';
import { ContentSpace } from '../_components';
import { locationActions } from '../_actions';
import { DataWrapper } from '../SettingsModule/SettingsModule';
import { ValidatedTextInput } from '../../Common/_components/ValidatedTextInput';
import { ValidationHelper } from '../_helpers';
import { SimpleToggle, CapsuleButton } from '../_components';
import { ClockinUser } from "../_models/ClockinUser";
import { User } from "../_models/User";
import { TimeSlotsSelector } from '../InvitationsModule/TimeSlotsSelector';
import { NFCCardUserConfiguration } from '../UsersModule/UserNfcModule'
import { CustomModal } from '../_components/CustomModal';
import { CustomNumberInput} from '../_components';
import { CustomDropdown } from '../_components/CustomDropdown';
import { FileUploadComponent } from "../_components";
import { Icons } from "../_assets";
import { AUTHORIZATION_TYPE, AUTHORIZATION_STATE } from '../_modelsTS/Authorization';

const TEST_IDS = {
    nameSection: "location-config-name-section",
    nameSaveButton: "location-config-name-save-button",
    nameInput: "location-config-name-input",

    companyNameSection: "location-config-company-name-section",
    companyNameSaveButton: "location-config-company-name-save-button",
    companyNameInput: "location-config-company-name-input",

    companyContactEmailSection: "location-config-company-contact-email-section",
    companyContactEmailSaveButton: "location-config-company-contact-email-save-button",
    companyContactEmailInput: "location-config-company-contact-email-input",

    companyLegalIdSection: "location-config-company-legal-id-section",
    companyLegalIdSaveButton: "location-config-company-legal-id-save-button",
    companyLegalIdInput: "location-config-company-legal-id-input",

    companyAdditionalInfoSection: "location-config-company-additional-info-section",
    companyAdditionalInfoSaveButton: "location-config-company-additional-info-save-button",
    companyAdditionalInfoInput: "location-config-company-additional-info-input",
};

ClockinsSettingsModule.TEST_IDS = TEST_IDS;

const locationInfoModel = LocationInfo();

export function ClockinsSettingsModule() {
    //LIBRARIES
    const { t } = useTranslation();
    const dispatch = useDispatch();

    //GLOBAL STATE
    const locationUid       = useSelector(Selectors.getSelectedLocationUid);
    const { begin, end }    = useSelector(Selectors.getClockinsDateFilter);
    const permission        = useSelector(Selectors.getPermissionSelectedLocation);
    const selectedUserUid   = useSelector(Selectors.getClockinsSelectedUserUid);
    const myUserUid         = useSelector(Selectors.getUserUid);
    const timezone          = useSelector(Selectors.getTimezone);
    let locationInfo        = locationInfoModel;
    locationInfo            = useSelector(Selectors.getLocationInfo);
    const workShifts        = useSelector(Selectors.getClockinsWorkShifts);
    const authorizations    = useSelector(Selectors.getAuthorizations);
    
    //GLOBAL ACTION HANDLERS
    const handleSelectUser    =         uid => dispatch(ClockinActions.selectUserUid(uid));
    const handleToggleAllowRemoteAccess = clockinUser => {
        dispatch(ClockinActions.toggleRemoteAccess(clockinUser, locationUid));
     
        setClockinUser(prevClockinUser => ({
            ...prevClockinUser, // 1. Copy top-level (id, settings)
                settings: {
                ...prevClockinUser.settings, // 2. Copy nested level (name, theme)
                allowsRemoteClockins: !clockinUser.settings.allowsRemoteClockins, // 3. Overwrite the target field
            }
        }));
    }

    const handleToggleFlexibleClockins = clockinUser =>
        dispatch(ClockinActions.toggleFlexibleClockins(clockinUser, locationUid))
    ;

      const handleUpdateClockinSettings = clockinUser =>
        dispatch(ClockinActions.updateUserClockinSettings(clockinUser, locationUid));
    ;

    const handleUploadContract = (file, onUpload) => {
        dispatch(ClockinActions.uploadContract(locationUid, selectedUserUid, file));
        onUpload();
    }

    //LOCAL STATE
    const [date, setDate] = useState(TimeHelper.getTodayIsoString());
    const canManageClockins = appPermissions.canManageClockins(permission);
    const [showModalRemoteAccess, setShowModalRemoteAccess] = useState(false);
    
    const changeRemoteAccess = () => {
        setShowModalRemoteAccess(true);
    };

    const changeFlexibleClockins = () => {
        handleToggleFlexibleClockins(clockinUser);

        setClockinUser(prevClockinUser => ({
            ...prevClockinUser, // 1. Copy top-level (id, settings)
                settings: {
                ...prevClockinUser.settings, // 2. Copy nested level (name, theme)
                hasFlexibleClockins: !clockinUser.settings.hasFlexibleClockins, // 3. Overwrite the target field
            }
        }));
    }

    const changeWeeklyClockinHours = (weeklyClockinHours) => {
        clockinUser.settings.weeklyClockinHours = weeklyClockinHours;
        handleUpdateClockinSettings(clockinUser);
        
        setClockinUser(prevClockinUser => ({
            ...prevClockinUser, // 1. Copy top-level (id, settings)
                settings: {
                ...prevClockinUser.settings, // 2. Copy nested level (name, theme)
                weeklyClockinHours: weeklyClockinHours, // 3. Overwrite the target field
            }
        }));
    }

    const changeMonthlyClockinHours = (monthlyClockinHours) => {
        clockinUser.settings.monthlyClockinHours = monthlyClockinHours;
        handleUpdateClockinSettings(clockinUser);
        
        setClockinUser(prevClockinUser => ({
            ...prevClockinUser, // 1. Copy top-level (id, settings)
                settings: {
                ...prevClockinUser.settings, // 2. Copy nested level (name, theme)
                monthlyClockinHours: monthlyClockinHours // 3. Overwrite the target field
            }
        }));
    }

    const changeWorkLocationPolicy = (workLocationPolicy) => {
        clockinUser.settings.workLocationPolicy = workLocationPolicy;
        handleUpdateClockinSettings(clockinUser);
        
        setClockinUser(prevClockinUser => ({
            ...prevClockinUser, // 1. Copy top-level (id, settings)
                settings: {
                ...prevClockinUser.settings, // 2. Copy nested level (name, theme)
                workLocationPolicy: workLocationPolicy // 3. Overwrite the target field
            }
        }));
    }

    const clockinUsers = useSelector(Selectors.getClockinsUsers);
    const users = useSelector(Selectors.getUsers);
    const [clockinUser, setClockinUser] = useState(ClockinUser());
    const [user, setUser] = useState(User());

    function handleOnUserSave() {
        dispatch(ClockinActions.listClockinUsers(locationUid, begin, end, canManageClockins));
        dispatch(locationActions.getLocationUsers(locationUid));
    }

    useEffect(() => {dispatch(locationActions.getLocationUsers(locationUid))}, [dispatch, locationUid])

    //INITIALIZATION
    useEffect(() => {
        dispatch(ClockinActions.selectUserUid(myUserUid));
    }, [dispatch, myUserUid]);

    useEffect(() => {
       dispatch(ClockinActions.listClockinUsers(locationUid, begin, end, canManageClockins));
    }, [dispatch, canManageClockins, begin, end, locationUid]);

    //EFFECTS
    useEffect(() => {
        if (!selectedUserUid)
            return;

        dispatch(ClockinActions.listAuthorizations());
    }, [dispatch, selectedUserUid]);

    //EFFECTS
    useEffect(() => {
        if (!selectedUserUid)
            return;

        clockinUsers.forEach((user) => {
            if (user.uid === selectedUserUid) {
                setClockinUser(user);
            }
        });
        users.forEach((user) => {
            if (user.uid === selectedUserUid) {
                setUser(user);
            }
        });
        dispatch(ClockinActions.setDateFilter({
            begin: TimeHelper.getFirstDayOfWeekForIso(date),
            end: TimeHelper.getLastDayOfWeekForIso(date),
        }));
        dispatch(ClockinActions.listWorkShifts());
    }, [dispatch, date, selectedUserUid]);

    useEffect(() => {
        clockinUsers.forEach((user) => {
            if (user.uid === selectedUserUid) {
                setClockinUser(user);
            }
        });
    }, [clockinUsers, selectedUserUid]);

    useEffect(() => {
        users.forEach((user) => {
            if (user.uid === selectedUserUid) {
                setUser(user);
            }
        });
    }, [users, selectedUserUid]);

    //VIEW
    return (
        <ContentSpace
            mainContent={
                <MainPageWithButtons
                    pageTitle={t("global_settings")}
                    content={content(handleOnUserSave)}
                    size={'LARGE'}
                />
            }
        />
    );

    function content(onUserSave) {
        return (
            <>
                <>
                        <div
                            className="
                        c-new-ft-m
                        text-uppercase
                        font-weight-bold
                        pt-2
                    "
                        >
                            {t("mod_clockins_company_settings")}
                        </div>

                        <CompanyNameInput
                            {...{
                                key: locationUid + "_company_name",
                                initialValue: locationInfo.companyName,
                                locationUid,
                            }}
                        />
                        <CompanyContactEmailInput
                            {...{
                                key: locationUid + "_company_contact_email",
                                initialValue: locationInfo.companyContactEmail,
                                locationUid,
                            }}
                        />
                        <CompanyLegalIdInput
                            {...{
                                key: locationUid + "_company_legal_id",
                                initialValue: locationInfo.companyCIF,
                                locationUid,
                            }}
                        />
                        <CompanyAdditionalInfoInput
                            {...{
                                key: locationUid + "_company_additional_info",
                                initialValue:
                                    locationInfo.companyAdditionalInfo,
                                locationUid,
                            }}
                        />

                        <div
                            className="
                        pt-2
                    "
                        >
                            {t("mod_clockin_user_disclaimer")}
                        </div>
                    </>
                
                <div className="mt-3" />
                <div className="
                    d-flex
                    no-gutters
                    overflow-auto
                " style={{height: 'calc(100% - 47px)'}}>
                    {canManageClockins && <UsersSection
                        isVisible={canManageClockins}
                        handleSelectUser={handleSelectUser}
                        handleToggleAllowRemoteAccess={handleToggleAllowRemoteAccess}
                        handleToggleFlexibleClockins={handleToggleFlexibleClockins}
                        selectedUserUid={selectedUserUid}
                    />}
                    {UserSettings(onUserSave)}
                </div>
                <CustomModal
                    show={showModalRemoteAccess}
                    centered
                    onHide={() => setShowModalRemoteAccess(false)}
                    title={clockinUser.settings.allowsRemoteClockins ? 
                        t("mod_clockins_modify_remote_clockins_disable"):
                        t("mod_clockins_modify_remote_clockins_enable")
                    }
                    content={clockinUser.settings.allowsRemoteClockins ? 
                        t("mod_clockins_modify_remote_clockins_advice_disable"): 
                        t("mod_clockins_modify_remote_clockins_advice_enable")
                    }
                    footer={
                        <>
                            <CapsuleButton
                                style="DANGER"
                                text={t("global_cancel")}
                                onClick={() => {
                                    setShowModalRemoteAccess(false);
                                }}
                            />
                            <CapsuleButton
                                isEnabled={true}
                                text={t("global_continue")}
                                onClick={() => {
                                    handleToggleAllowRemoteAccess(clockinUser);
                                    setShowModalRemoteAccess(false);
                                }}
                            />
                        </>
                    }
                />
            </>
        );
    }

    function CompanyNameInput({ initialValue, locationUid }) {

        //LIBRARIES
        const { t } = useTranslation();
        const dispatch = useDispatch();
    
        //ACTION HADLERS
        function handleSave(text) {
            dispatch(locationActions.updateCompanyName(text, locationUid));
        }
    
        return (
            <DataWrapper>
                <div
                    data-testid={TEST_IDS.companyNameSection}
                    className="d-flex align-items-center justify-content-between"
                >
                    <div>
                        {t("mod_clockins_company_name")}
                    </div>
                    <InlineValidatedTextInput
                        inputTestID={TEST_IDS.companyNameInput}
                        saveButtonTestID={TEST_IDS.companyNameSaveButton}
                        handleSave={handleSave}
                        initialValue={initialValue}
                        required={false}
                    />
                </div>
            </DataWrapper>
        );
    }
    
    function CompanyContactEmailInput({ initialValue, locationUid }) {
    
        //LIBRARIES
        const { t } = useTranslation();
        const dispatch = useDispatch();
    
        //ACTION HADLERS
        function handleSave(text) {
            dispatch(locationActions.updateCompanyContactEmail(text, locationUid));
        }
    
        return (
            <DataWrapper>
                <div
                    data-testid={TEST_IDS.companyContactEmailSection}
                    className="d-flex align-items-center justify-content-between"
                >
                    <div>
                        {t("mod_clockins_company_contact_email")}
                    </div>
                    <InlineValidatedMailInput
                        inputTestID={TEST_IDS.CompanyContactEmailInput}
                        saveButtonTestID={TEST_IDS.companyContactEmailSaveButton}
                        handleSave={handleSave}
                        initialValue={initialValue}
                        required={false}
                    />
                </div>
            </DataWrapper>
        );
    }
    
    function CompanyLegalIdInput({ initialValue, locationUid }) {
    
        //LIBRARIES
        const { t } = useTranslation();
        const dispatch = useDispatch();
    
        //ACTION HADLERS
        function handleSave(text) {
            dispatch(locationActions.updateCompanyLegalId(text, locationUid));
        }
    
        return (
            <DataWrapper>
                <div
                    data-testid={TEST_IDS.companyLegalIdSection}
                    className="d-flex align-items-center justify-content-between"
                >
                    <div>
                        {t("mod_clockins_company_legal_id")}
                    </div>
                    <InlineValidatedTextInput
                        inputTestID={TEST_IDS.companyLegalIdInput}
                        saveButtonTestID={TEST_IDS.companyLegalIdSaveButton}
                        handleSave={handleSave}
                        initialValue={initialValue}
                        required={false}
                    />
                </div>
            </DataWrapper>
        );
    }
    
    function CompanyAdditionalInfoInput({ initialValue, locationUid }) {
    
        //LIBRARIES
        const { t } = useTranslation();
        const dispatch = useDispatch();
    
        //ACTION HADLERS
        function handleSave(text) {
            dispatch(locationActions.updateCompanyAdditionalInfo(text, locationUid));
        }
    
        return (
            <DataWrapper>
                <div
                    data-testid={TEST_IDS.companyAdditionalInfoSection}
                    className="d-flex align-items-center justify-content-between"
                >
                    <div>
                        {t("mod_clockins_company_additional_info")}
                    </div>
                    <InlineValidatedTextInput
                        inputTestID={TEST_IDS.companyAdditionalInfoInput}
                        saveButtonTestID={TEST_IDS.companyAdditionalInfoSaveButton}
                        handleSave={handleSave}
                        initialValue={initialValue}
                        required={false}
                    />
                </div>
            </DataWrapper>
        );
    }
    
    function InlineValidatedTextInput({
        inputTestID,
        saveButtonTestID,
    
        handleSave,
        initialValue,
    
        required,
    } = {}) {
    
        //LOCAL STATE
        const [text, setText] = useState(initialValue);
        const [isValid, setIsValid] = useState(false);
        const [isEdited, setIsEdited] = useState(false);
        
        //OVERRIDING
    
        useEffect(() => {
            setIsEdited(text !== initialValue);
        }, [text, initialValue]);
    
        //ACTION HADLERS
        function handleDiscardChanges() {
            setText(initialValue);
        }
    
        function handleSaveChanges() {
            handleSave(text);
        }
    
        return (
            <div className="d-flex">
                {isEdited &&
                    <InlineEditButtons
                        acceptButtonTestID={saveButtonTestID}
                        isEnabled={isValid}
                        handleDiscardChanges={handleDiscardChanges}
                        handleConfirmChanges={handleSaveChanges}
                    />
                }
                <ValidatedTextInput
                    isRequired={required}
                    isEditable
    
                    testID={inputTestID}
                    value={text}
                    setValue={setText}
                    setIsValid={setIsValid}
                />
            </div>
        );
    }

    function InlineValidatedMailInput({
        inputTestID,
        saveButtonTestID,
    
        handleSave,
        initialValue,
    
        required,
    } = {}) {
    
        //LOCAL STATE
        const [text, setText] = useState(initialValue);
        const [isValid, setIsValid] = useState(false);
        const [isEdited, setIsEdited] = useState(false);
        
        //OVERRIDING
    
        useEffect(() => {
            setIsEdited(text !== initialValue);
        }, [text, initialValue]);
    
        //ACTION HADLERS
        function handleDiscardChanges() {
            setText(initialValue);
        }
    
        function handleSaveChanges() {
            handleSave(text);
        }
    
        function checkTextIsValidMail() {
            if  (!text || ValidationHelper.ValidationFns.isEmailValid(text)) setIsValid(true);
            else setIsValid(false);
        }
    
        return (
            <div className="d-flex">
                {isEdited &&
                    <InlineEditButtons
                        acceptButtonTestID={saveButtonTestID}
                        isEnabled={isValid}
                        handleDiscardChanges={handleDiscardChanges}
                        handleConfirmChanges={handleSaveChanges}
                    />
                }
                <ValidatedTextInput
                    isRequired={required}
                    isEditable
    
                    testID={inputTestID}
                    value={text}
                    setValue={setText}
                    setIsValid={checkTextIsValidMail}
                />
            </div>
        );
    }

    function UserSettings(onUserSave) {
        return (
            <div className="
                w-100
                h-100
                p-3
                d-flex
                flex-column
            ">
                <div>
                    <div className="text-primary c-line-height-1 c-ft-s ">
                        {t("mod_clockin_clockin_configuration")}
                    </div>
                    <div className='mt-2'/>
                    <div
                        className="
                        d-flex
                        align-items-center
                        py-1
                    "
                        onClick={(e) => e.stopPropagation()}
                    >
                        <SimpleToggle
                            checked={clockinUser.settings.allowsRemoteClockins}
                            size="X_SMALL"
                            handleChange={changeRemoteAccess}
                        />
                        <div className="ml-1" />
                        <div className="text-secondary c-line-height-1">
                            {t("mod_clockins_remote_clockins")}
                        </div>
                    </div>
                    <div className='mt-2' />
                    <div
                        className="
                        d-flex
                        align-items-center
                        py-1
                    "
                        onClick={(e) => e.stopPropagation()}
                    >
                        <SimpleToggle
                            checked={clockinUser.settings.hasFlexibleClockins}
                            size="X_SMALL"
                            handleChange={changeFlexibleClockins}
                        />
                        <div className="ml-1" />
                        <div className="text-secondary c-line-height-1">
                            {t("mod_clockins_flexible_clockins")}
                        </div>
                    </div>
                </div>
                <div className='my-1' />
                <div
                    className="
                    d-flex
                    align-items-center
                    py-1
                ">
                    <CustomNumberInput
                        onChange={changeWeeklyClockinHours}
                        min={0}
                        max={50}
                        value={clockinUser.settings.weeklyClockinHours}
                        showButtons={false}
                    />
                    <div className="ml-2" />
                    <div className="text-secondary c-line-height-1">
                        {t("mod_clockin_weekly_working_hours")}
                    </div>
                </div>
                <div className='mt-1' />
                <div
                    className="
                    d-flex
                    align-items-center
                    py-1
                ">
                    <CustomNumberInput
                        onChange={changeMonthlyClockinHours}
                        min={0}
                        max={200}
                        value={clockinUser.settings.monthlyClockinHours}
                        showButtons={false}
                    />
                    <div className="ml-2" />
                    <div className="text-secondary c-line-height-1">
                        {t("mod_clockin_monthly_working_hours")}
                    </div>
                </div>
                <div className='my-2' />
                <div className='py-2'>
                    <div className="text-secondary c-line-height-1 mb-2">
                        {t("mod_clockin_work_location_policy")}
                    </div>
                    <CustomDropdown
                        options={buildWorkLocationPolicyList()}
                        onChange={onSelectWorkLocationPolicy}
                        value={clockinUser.settings.workLocationPolicy}
                    />
                </div>
                <div className='my-2' />
                <div>
                    <div className="text-secondary c-line-height-1">
                        {t("mod_clockin_employment_contract")}
                    </div>
                    <div className="mb-1" />
                    <JobContractComponent selectedUserUid={selectedUserUid} />
                </div>
                <div className='my-2' />
                <div>
                    <div className="text-secondary c-line-height-1">
                        {t("mod_clockins_authorizations")}
                    </div>
                    <div className="mb-1" />
                    {AuthorizationsComponent()}
                </div>
                <div className='mt-5' />
                <div>
                    <div className="text-primary c-line-height-1 c-ft-s">
                        {t("mod_clockins_work_shifts")}
                    </div>
                    <div className="mt-2">
                        <TimeSlotsSelector
                            onChange={onChangeTimeSlot}
                            weekDaysTimeSlots={
                                workShifts
                            }
                        />
                    </div>
                </div>
                {user.isNFCCard &&
                    <div className='mt-5'>
                        <div className="text-primary c-line-height-1 c-ft-s">
                            {t("global_nfc_card_user_configuration")}
                        </div>
                        <div className="mt-2">
                            <NFCCardUserConfiguration user={user} onSave={onUserSave}/>
                        </div>
                    </div>
                }
            </div>
        );
    }

    function downloadFile(url) {
        window.open(url, '_blank');
    }

    function JobContractComponent({selectedUserUid}) {
        const [isEditing, setIsEditing] = useState(false);
        const [uploaded, setUploaded] = useState(false);

        const onUpload = () => {
            setIsEditing(false);
            setUploaded(true);
        }

        if (isEditing || (!clockinUser.settings.jobContractUrl && !uploaded)) {
            return (
                <div className='mt-2'>
                    {isEditing && <CapsuleButton
                        isEnabled={true}
                        text={t("webapp_global_cancel")}
                        onClick={() => {setIsEditing(false);}}
                        size={'SMALL'}
                        icon={Icons.cross}
                        fillStyle={"OUTLINED"}
                        style={"DANGER"}
                        textUppercase={false}  
                    />}
                    <div className='my-1'></div>
                    <FileUploadComponent
                        onUpload={(file) => handleUploadContract(file, onUpload)}
                        isUploading={false}
                        fileType = 'application/pdf'
                    />
                </div>
            )
        }

        return (
            <>
                <div className="my-2 d-flex">
                    <CapsuleButton
                        text={t("global_view_pdf")}
                        onClick={() => {downloadFile(clockinUser.settings.jobContractUrl)}}
                        size={'SMALL'}    
                        fillStyle={"FILLED"}
                    />
                    <div className='ml-2'></div>
                    <CapsuleButton
                        text={t("mod_clockins_update_contract")}
                        onClick={() => {setIsEditing(true)}}
                        size={'SMALL'}    
                        fillStyle={"OUTLINED"}
                    />
                </div>

            </>
        );
    }

   function AuthorizationComponent({authorization}) {
        const { t } = useTranslation();

        const createOnUiTime = TimeHelper.localizeIsoStringToFormat(
            authorization.createdOn,
            timezone,
            TimeHelper.getTimeFormats().DATE_NO_TIME,
        );
        let revokedOnUiTime = TimeHelper.localizeIsoStringToFormat(
            authorization.revokedOn,
            timezone,
            TimeHelper.getTimeFormats().DATE,
        );
        let sigendOnUiTime = TimeHelper.localizeIsoStringToFormat(
            authorization.signedOn,
            timezone,
            TimeHelper.getTimeFormats().DATE,
        );

        let color = "text-primary";
        if (authorization.state === AUTHORIZATION_STATE.REVOKED) {
            color = "text-danger";
        } else if (authorization.state === AUTHORIZATION_STATE.PENDING) {
            color = "text-secondary";
        }

        return (
            <div className='d-flex'>
                <a href={authorization.documentUrl} target="_blank" rel="noopener noreferrer" className={color} style={{textDecoration: "underline"}}>
                    <span>
                        {t("mod_clockins_authorization")} {createOnUiTime},&nbsp;
                        {authorization.state === AUTHORIZATION_STATE.PENDING && <>{t("mod_clockins_authorization_pending")}</>}
                        {authorization.state === AUTHORIZATION_STATE.SIGNED && <>{t("mod_clockins_authorization_signed")}</>}
                        {authorization.state === AUTHORIZATION_STATE.REVOKED && <>{t("mod_clockins_authorization_revoked_on")} {revokedOnUiTime}</>}
                    </span>
                </a>
            </div>
        );
    }

    function AuthorizationsComponent() {
        const { t } = useTranslation();

        if (authorizations.length === 0) {
            return <>{t("mod_clockins_authorization_no_authorizations")}</>
        }

        return (
            <div>
                {authorizations
                .filter(authorization => authorization.type === AUTHORIZATION_TYPE.CLOCKINS_PERSONAL_PHONE)
                .map((authorization) => (
                    <AuthorizationComponent authorization={authorization} />
                ))}
            </div>
        );
    }

    function onSelectWorkLocationPolicy(workLocationPolicy) {
        changeWorkLocationPolicy(workLocationPolicy);
    }

    function buildWorkLocationPolicyList() {
        const workLocationPolicies = [
            {id: 0, name: t("mod_clockin_work_location_policy_not_set")},
            {id: 1, name: t("mod_clockin_work_location_policy_on_site")},
            {id: 2, name: t("mod_clockin_work_location_policy_remote")},
            {id: 3, name: t("mod_clockin_work_location_policy_hybrid")},
        ]

        return workLocationPolicies.map(workLocationPolicy =>
            CustomDropdown.buildOption(
                workLocationPolicy.id,
                workLocationPolicy.name,
            )
        );
    }

    function onChangeTimeSlot(weekDays) {
        if (workShifts === null) {
            dispatch(ClockinActions.addWorkShiftSlot(weekDays));
            return;
        }
        
        if (weekDays !== null) {
            Object.keys(weekDays).map((currentKey) => {
                if (workShifts[currentKey] === undefined) {
                    addSlot(currentKey);
                } else if (workShifts[currentKey].length < weekDays[currentKey].length) {
                    // Add slot
                    addSlot(currentKey);
                } else if (workShifts[currentKey].length > weekDays[currentKey].length) {
                    deleteSlot(currentKey);
                }
            }, []);
        }

        function addSlot(currentKey) {
            // Add slot
            const slots = [];
            weekDays[currentKey].forEach((slot) => {
                if (slot.uid === undefined) {
                    slots.push(slot);
                }
            });
            const newSlots = {};
            newSlots[currentKey] = slots;
            dispatch(ClockinActions.addWorkShiftSlot(newSlots));
        }

        function deleteSlot(currentKey) {
            workShifts[currentKey].forEach((slot) => {
                const uid = slot.uid;
                let included = false;
                weekDays[currentKey].forEach((slot) => {
                    if (slot.uid === uid) {
                        included = true;
                    }
                });

                if (!included) {
                    dispatch(ClockinActions.deleteWorkShiftSlot(uid));
                }
            });
        }
    }
}

function UsersSection({
    selectedUserUid,
    handleSelectUser = () => {},
    handleToggleAllowRemoteAccess,
    handleToggleFlexibleClockins,
} = {}) {


    const clockinUsers      = useSelector(Selectors.getClockinsUsers);
    const filter            = useSelector(Selectors.getClockinsFilter);
    const users = useMemo(() => filterClockinUsers(clockinUsers, filter),[clockinUsers, filter]) 

    return (
        <>
            <div className="
            c-w-22rem
            c-max-w-22rem
            d-flex
            flex-column
            ">
                <ClockinsFilters
                    filter={filter}
                    users={clockinUsers}
                />
                <div className="overflow-auto h-100">
                    <UsersSettingsColumn {...{
                        users,
                        selectedUid: selectedUserUid,
                        handleSelectUser,
                        handleToggleAllowRemoteAccess,
                        handleToggleFlexibleClockins,
                    }}
                    />
                </div>
            </div>
        <div className="mx-1 border-right"/>
        </>
    );

    function filterClockinUsers(clockinUsers, filter) {
        let clockinUsersFiltered = [...clockinUsers];
        if (filter.value.user !== FILTER_DISABLED_ALL.key) {
            clockinUsersFiltered = clockinUsersFiltered.filter(user => user.uid === filter.value.user);
        }

        return clockinUsersFiltered;
    }

}
